import React, { useState, useEffect } from "react";
import "../Packages/Packages.css";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import LinkIcon from "@mui/icons-material/Link";
import PeopleIcon from "@mui/icons-material/People";
import AxiosUrl from "../AxiosUrl";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/**
 * Wallet top-up coupons (backend models/Coupon.js, /admin/coupons/:uid).
 * A member types the code in the app's "Add money" sheet, pays the full amount by
 * bKash and gets the bonus on top in the wallet. A used coupon can only be
 * switched off, not deleted, so its history stays readable.
 */

const EMPTY_FORM = { id: null, code: "", type: "percent", value: "", maxBonus: "", note: "", active: true };

const taka = (n) => `৳${Number(n || 0).toLocaleString("en-US")}`;

/** Same rule as the backend (utils/Coupon/walletCoupon.js couponBonus) */
const bonusFor = (coupon, amount) => {
  const value = Number(coupon.value);
  if (!(value > 0) || !(amount > 0)) return 0;
  if (coupon.type === "fixed") return Math.floor(value);
  const bonus = Math.floor((amount * Math.min(value, 100)) / 100);
  const cap = Number(coupon.maxBonus);
  return cap > 0 ? Math.min(bonus, Math.floor(cap)) : bonus;
};

const describe = (coupon) =>
  coupon.type === "fixed"
    ? `${taka(coupon.value)} bonus`
    : `${coupon.value}% bonus${coupon.maxBonus ? ` (max ${taka(coupon.maxBonus)})` : ""}`;

/** Opens the app's wallet with the add-money sheet and the coupon filled in */
const couponLink = (code) => `https://bibahobangla.com/wallet?addMoney=1&coupon=${encodeURIComponent(code)}`;

function Coupons() {
  const uid = localStorage.getItem("id");
  const token = localStorage.getItem("token");

  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const [usage, setUsage] = useState(null); // { code, redemptions } for the usage dialog

  useEffect(() => {
    fetchCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const response = await AxiosUrl.get(`/admin/coupons/${uid}`, { headers: { token } });
      if (response.data.Error) {
        toast.error(response.data.Error);
      } else {
        setCoupons(response.data.coupons || []);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch coupons");
    } finally {
      setLoading(false);
    }
  };

  const saveCoupon = async (body, { closeDialog = false } = {}) => {
    setSaving(true);
    try {
      const response = await AxiosUrl.post(`/admin/coupons/${uid}`, body, { headers: { token } });
      if (response.data.Error) {
        toast.error(response.data.Error);
        return false;
      }
      toast.success(response.data.Message);
      if (closeDialog) setDialogOpen(false);
      fetchCoupons();
      return true;
    } catch (error) {
      console.error(error);
      toast.error("Failed to save coupon");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (coupon) => {
    setForm({
      id: coupon._id,
      code: coupon.code,
      type: coupon.type,
      value: coupon.value,
      maxBonus: coupon.maxBonus ?? "",
      note: coupon.note || "",
      active: !!coupon.active,
    });
    setDialogOpen(true);
  };

  const handleSave = () => {
    const body = {
      type: form.type,
      value: form.value,
      maxBonus: form.type === "percent" ? form.maxBonus : null,
      note: form.note,
      active: form.active,
      ...(form.id ? { id: form.id } : { code: form.code }),
    };
    saveCoupon(body, { closeDialog: true });
  };

  const toggleActive = (coupon) =>
    saveCoupon({
      id: coupon._id,
      type: coupon.type,
      value: coupon.value,
      maxBonus: coupon.maxBonus,
      note: coupon.note,
      active: !coupon.active,
    });

  const deleteCoupon = async (coupon) => {
    if (!window.confirm(`Delete coupon ${coupon.code}?`)) return;
    try {
      const response = await AxiosUrl.delete(`/admin/coupons/${uid}/${coupon._id}`, { headers: { token } });
      if (response.data.Error) {
        toast.error(response.data.Error);
      } else {
        toast.success(response.data.Message);
        fetchCoupons();
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete coupon");
    }
  };

  const showUsage = async (coupon) => {
    try {
      const response = await AxiosUrl.get(`/admin/coupons/${uid}/${coupon._id}/redemptions`, { headers: { token } });
      if (response.data.Error) {
        toast.error(response.data.Error);
      } else {
        setUsage({ code: response.data.code, redemptions: response.data.redemptions || [] });
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to load coupon usage");
    }
  };

  const copyLink = async (code) => {
    try {
      await navigator.clipboard.writeText(couponLink(code));
      toast.success("Link copied");
    } catch {
      window.prompt("Copy this link", couponLink(code));
    }
  };

  const activeCount = coupons.filter((c) => c.active).length;
  const totalUses = coupons.reduce((sum, c) => sum + (c.usedCount || 0), 0);
  const totalBonus = coupons.reduce((sum, c) => sum + (c.totalBonus || 0), 0);

  const formCodeOk = !!form.id || /^[A-Za-z0-9_-]{3,24}$/.test(form.code.trim());
  const formValue = Number(form.value);
  const formOk = formCodeOk && formValue > 0 && (form.type !== "percent" || formValue <= 100);
  const example = 1000;
  const exampleBonus = formOk ? bonusFor(form, example) : 0;

  return (
    <div className="packages-container">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="packages-header">
        <div>
          <h1>Wallet Coupons</h1>
          <p style={{ margin: "6px 0 0", color: "#666", fontSize: 14 }}>
            Members type the code in the app when adding money by bKash. They pay the full amount and get the bonus on
            top in their wallet.
          </p>
        </div>
        <div className="header-actions">
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openCreate}>
            New Coupon
          </Button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
        {[
          ["Active coupons", activeCount],
          ["Times used", totalUses],
          ["Bonus given", taka(totalBonus)],
        ].map(([label, value]) => (
          <Paper key={label} style={{ padding: "14px 20px", minWidth: 160 }}>
            <div style={{ fontSize: 13, color: "#666" }}>{label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#333", marginTop: 4 }}>{value}</div>
          </Paper>
        ))}
      </div>

      {loading ? (
        <div className="loading">Loading coupons...</div>
      ) : (
        <TableContainer component={Paper} className="table-container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Code</strong></TableCell>
                <TableCell><strong>Bonus</strong></TableCell>
                <TableCell><strong>Note</strong></TableCell>
                <TableCell align="center"><strong>Active</strong></TableCell>
                <TableCell align="right"><strong>Used</strong></TableCell>
                <TableCell align="right"><strong>Bonus given</strong></TableCell>
                <TableCell><strong>Created</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coupons.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No coupons yet. Create one to offer a recharge bonus.
                  </TableCell>
                </TableRow>
              ) : (
                coupons.map((coupon) => (
                  <TableRow key={coupon._id}>
                    <TableCell style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 15 }}>{coupon.code}</TableCell>
                    <TableCell>{describe(coupon)}</TableCell>
                    <TableCell style={{ color: "#666", maxWidth: 240 }}>{coupon.note || "—"}</TableCell>
                    <TableCell align="center">
                      <Switch checked={!!coupon.active} onChange={() => toggleActive(coupon)} disabled={saving} />
                    </TableCell>
                    <TableCell align="right">{coupon.usedCount || 0}</TableCell>
                    <TableCell align="right">{taka(coupon.totalBonus)}</TableCell>
                    <TableCell>{coupon.createdAt ? new Date(coupon.createdAt).toLocaleDateString("en-GB") : "—"}</TableCell>
                    <TableCell align="center" style={{ whiteSpace: "nowrap" }}>
                      <Tooltip title="Copy app link: opens Add money with this coupon in the app (versions after 1.0.73; the web wallet has no coupon field)">
                        <IconButton onClick={() => copyLink(coupon.code)}>
                          <LinkIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Who used it">
                        <IconButton onClick={() => showUsage(coupon)}>
                          <PeopleIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton color="primary" onClick={() => openEdit(coupon)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={coupon.usedCount ? "Used coupons can only be switched off" : "Delete"}>
                        <span>
                          <IconButton color="error" onClick={() => deleteCoupon(coupon)} disabled={!!coupon.usedCount}>
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create / edit */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{form.id ? `Edit ${form.code}` : "New Coupon"}</DialogTitle>
        <DialogContent>
          <TextField
            label="Code"
            fullWidth
            margin="normal"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, "") })}
            disabled={!!form.id}
            inputProps={{ maxLength: 24, style: { fontFamily: "monospace", letterSpacing: 1 } }}
            helperText={form.id ? "The code can't be changed" : "3-24 letters, digits, - or _ (e.g. EID100)"}
          />
          <TextField
            select
            label="Bonus type"
            fullWidth
            margin="normal"
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
          >
            <MenuItem value="percent">Percent of the amount added</MenuItem>
            <MenuItem value="fixed">Fixed amount (৳)</MenuItem>
          </TextField>
          <div style={{ display: "flex", gap: 10 }}>
            <TextField
              label={form.type === "percent" ? "Bonus (%)" : "Bonus (৳)"}
              type="number"
              margin="normal"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
              inputProps={{ min: 1, max: form.type === "percent" ? 100 : undefined }}
              style={{ flex: 1 }}
            />
            {form.type === "percent" && (
              <TextField
                label="Max bonus (৳)"
                type="number"
                margin="normal"
                value={form.maxBonus}
                onChange={(e) => setForm({ ...form, maxBonus: e.target.value })}
                inputProps={{ min: 0 }}
                helperText="Empty = no limit"
                style={{ flex: 1 }}
              />
            )}
          </div>
          <TextField
            label="Note (staff only)"
            fullWidth
            margin="normal"
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            inputProps={{ maxLength: 200 }}
            helperText="e.g. Eid offer SMS, 2026"
          />
          <FormControlLabel
            control={<Switch checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />}
            label="Active (members can use it)"
          />
          {formOk && (
            <p style={{ margin: "12px 0 0", padding: "10px 12px", background: "#f0fdf4", borderRadius: 8, color: "#166534" }}>
              Example: a {taka(example)} recharge gets +{taka(exampleBonus)}, so {taka(example + exampleBonus)} reaches
              the wallet.
            </p>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary" disabled={!formOk || saving}>
            {form.id ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Usage */}
      <Dialog open={!!usage} onClose={() => setUsage(null)} maxWidth="md" fullWidth>
        <DialogTitle>{usage ? `${usage.code}: paid recharges` : ""}</DialogTitle>
        <DialogContent>
          {usage && usage.redemptions.length === 0 ? (
            <p style={{ color: "#666" }}>Nobody has used this coupon yet.</p>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Member</strong></TableCell>
                  <TableCell><strong>Mobile</strong></TableCell>
                  <TableCell align="right"><strong>Paid</strong></TableCell>
                  <TableCell align="right"><strong>Bonus</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(usage?.redemptions || []).map((r) => (
                  <TableRow key={r._id}>
                    <TableCell>
                      {r.username || "—"}
                      {r.uniqueId ? <span style={{ color: "#999" }}> ({r.uniqueId})</span> : null}
                    </TableCell>
                    <TableCell>{r.mobile || "—"}</TableCell>
                    <TableCell align="right">{taka(r.amount)}</TableCell>
                    <TableCell align="right">+{taka(r.bonus)}</TableCell>
                    <TableCell>{new Date(r.date).toLocaleString("en-GB")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsage(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default Coupons;
